"use client"

import { useState, useEffect } from "react"
import { Scale, RefreshCw, ArrowRight } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"

type HeroMockupProps = {
  /** Подпись под мокапом (по умолчанию — про пять экранов). */
  caption?: string
}

export function HeroMockup({ caption = "одно движение — пять экранов" }: HeroMockupProps) {
  const [step, setStep] = useState(0)

  useEffect(() => {
    // Анимация шагов:
    // 0: Пусто
    // 1: Юрист пишет факт
    // 2: Агент отвечает
    // 3: Юрист прикрепляет документ
    // 4: Агент сверяет
    // 5: Магия: факт доказан, появляется провенанс
    const t1 = setTimeout(() => setStep(1), 1000)
    const t2 = setTimeout(() => setStep(2), 2500)
    const t3 = setTimeout(() => setStep(3), 4000)
    const t4 = setTimeout(() => setStep(4), 5500)
    const t5 = setTimeout(() => setStep(5), 7500)
    
    return () => {
      clearTimeout(t1)
      clearTimeout(t2)
      clearTimeout(t3)
      clearTimeout(t4)
      clearTimeout(t5)
    }
  }, [])

  return (
    <div className="relative">
      <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-2xl shadow-primary/10">
        <div className="flex items-center gap-1.5 border-b border-border bg-surface px-4 py-3">
          <div className="size-2.5 rounded-full bg-destructive/60" />
          <div className="size-2.5 rounded-full bg-warning/70" />
          <div className="size-2.5 rounded-full bg-success/70" />
          <span className="ml-3 truncate text-xs text-muted-foreground">Дело №283/2026 — поставка бетона B25</span>
        </div>
        
        {/* Mobile: snap-x scroll, Desktop: grid cols-5 */}
        <div className="flex overflow-x-auto snap-x snap-mandatory md:grid md:grid-cols-5 md:overflow-visible [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          
          {/* Chat */}
          <div className="w-full shrink-0 snap-center space-y-3 border-r border-border p-4 md:col-span-2">
            <div className={cn(
              "rounded-lg rounded-tl-sm bg-secondary px-3 py-2 text-xs text-secondary-foreground transition-all duration-500",
              step >= 1 ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0"
            )}>
              Аванс был 30% — 1,44 млн
            </div>
            
            <div className={cn(
              "rounded-lg rounded-tl-sm bg-primary px-3 py-2 text-xs text-primary-foreground transition-all duration-500",
              step >= 2 ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0"
            )}>
              Записал факт. Прикрепите платёжку — подтвердим.
            </div>
            
            <div className={cn(
              "rounded-lg rounded-tl-sm bg-secondary px-3 py-2 text-xs text-secondary-foreground transition-all duration-500",
              step >= 3 ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0"
            )}>
              Загружаю платёжку аванса
            </div>
            
            <div className={cn(
              "flex items-center gap-2 text-xs text-muted-foreground transition-all duration-500",
              step >= 4 ? "opacity-100" : "opacity-0"
            )}>
              <span className="size-1.5 animate-pulse rounded-full bg-primary" />
              {step >= 5 ? "Сверка завершена" : "сверяю с договором…"}
            </div>
          </div>
          
          {/* Canvas */}
          <div className="w-full shrink-0 snap-center space-y-3 p-4 md:col-span-3">
            <div className="flex items-center gap-2 text-xs font-medium text-foreground">
              <Scale className="size-3.5 text-primary" /> Ось доказывания
            </div>
            
            <div className={cn(
              "rounded-lg border border-border bg-background p-3 transition-colors duration-500",
              step >= 5 && "border-success/30 bg-success/5"
            )}>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Аванс 30% оплачен</span>
                {step >= 5 ? (
                  <Badge variant="default" className="scale-100 bg-success transition-all duration-300">доказано</Badge>
                ) : step >= 1 ? (
                  <Badge variant="secondary" className="scale-100 transition-all duration-300">со слов</Badge>
                ) : (
                  <Badge variant="outline" className="scale-100 border-dashed text-muted-foreground/50 transition-all duration-300">нет данных</Badge>
                )}
              </div>
              <div className={cn(
                "text-[11px] text-muted-foreground transition-all duration-500",
                step >= 5 ? "mt-2 max-h-10 opacity-100" : "mt-0 max-h-0 overflow-hidden opacity-0"
              )}>
                из платёжки №112 → договор №283, п.3.2
              </div>
            </div>
            
            <div className="rounded-lg border border-border bg-background p-3">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Качество B25</span>
                <Badge variant="secondary">проверяется</Badge>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-lg border border-border bg-background p-2 text-[11px] text-muted-foreground">
                Неустойка: <span className="font-medium text-foreground">432 000 ₽</span>
              </div>
              <div className="rounded-lg border border-border bg-background p-2 text-[11px] text-muted-foreground">
                Госпошлина: <span className="font-medium text-foreground">25 960 ₽</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Mobile hint for scroll */}
        <div className="flex items-center justify-center gap-1 border-t border-border bg-muted/30 p-2 text-center text-[10px] text-muted-foreground md:hidden">
          <ArrowRight className="size-3" /> Свайпните, чтобы увидеть канвас
        </div>
      </div>
      
      <div className={cn(
        "absolute bottom-0 left-0 hidden rounded-xl border border-border bg-card px-4 py-3 shadow-lg transition-all delay-500 duration-700 md:block",
        step >= 5 ? "translate-y-4 opacity-100" : "translate-y-8 opacity-0"
      )}>
        <div className="flex items-center gap-2 text-xs font-medium text-foreground">
          <RefreshCw className="size-3.5 text-accent" />
          {caption}
        </div>
      </div>
    </div>
  )
}
